import {
  createRootRouteWithContext,
  HeadContent,
  Link,
  Scripts,
} from "@tanstack/react-router";
import { Outlet } from "@tanstack/react-router";
import TopLoader from "../components/top-loader";
import { TanStackRouterDevtools } from "@tanstack/react-router-devtools";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { manifest, seo } from "@/lib/seo";
import styles from "@/index.css?url";
import Client from "@/entry-client?url";
import { generateKeywordsMetaTag } from "@/utils/keywords";
import { InkrivCookieBanner } from "@/components/cookie-banner";
import { Navbar } from "@/components/landing/navbar";
import { Footer } from "@/components/landing/footer";

type RouterContext = {
  isAuthenticated: boolean;
  queryClient?: QueryClient;
  isLoading: boolean;
  head: string;
  helmetContext: object;
  manifest: Record<string, any>;
};

const queryClient = new QueryClient();

const isDev = import.meta.env.DEV ?? true;

export const Route = createRootRouteWithContext<RouterContext>()({
  component: () => (
    <>
      <html lang="en">
        <head>
          <HeadContent />
        </head>
        <body>
          <InkrivCookieBanner />
          <QueryClientProvider client={queryClient}>
            <TopLoader />
            <Outlet />
            {isDev && <TanStackRouterDevtools position="bottom-right" />}
            <Scripts />
          </QueryClientProvider>
        </body>
      </html>
    </>
  ),
  notFoundComponent() {
    return (
      <div>
        <Navbar />
        <div className="min-h-[100vh] bg-gray-25 flex flex-col items-center justify-center px-4">
          <div className="max-w-lg w-full bg-white rounded-lg shadow-lg overflow-hidden">
            <div className="p-6 sm:p-8">
              {/* Status Code */}
              <h1 className="text-6xl sm:text-8xl font-bold text-secondary-600 text-center">
                404
              </h1>

              <h2 className="mt-6 text-2xl font-semibold text-gray-900 text-center">
                Page Not Found
              </h2>

              <p className="mt-4 text-gray-600 text-center">
                The page you are looking for doesn't exist or has been moved.
              </p>

              <p className="mt-2 text-gray-500 text-center">
                Please check the URL or navigate back to our homepage.
              </p>

              <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/"
                  className="px-6 py-2 bg-secondary-600 hover:bg-secondary-700 text-white font-medium rounded-md text-center transition-colors duration-300 text-xs"
                  replace
                >
                  Back to Home
                </Link>

                <button
                  onClick={() => window.history.back()}
                  className="px-6 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 font-medium rounded-md text-center transition-colors duration-300 text-xs"
                >
                  Go Back
                </button>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  },
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      ...seo({
        title: "Inkriv - AI Writing Assistant That writes like you",
        description:
          "Inkriv helps you write articles, essays, and newsletters faster with AI that researches, drafts, and polishes content in your unique voice—while learning your tone as you write.",
        image:
          "https://res.cloudinary.com/dtcejk6h3/image/upload/v1747040895/og-image_ztxfrr.png",
        keywords: generateKeywordsMetaTag(["core", "features", "benefits"], 7),
      }),
    ],
    links: [...manifest, { rel: "stylesheet", href: styles }],
    scripts: [
      {
        async: true,
        src: "https://www.googletagmanager.com/gtag/js?id=G-K0S1ZLV153",
      },
      {
        children: `
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          gtag('js', new Date());
          gtag('config', 'G-K0S1ZLV153');
        `,
      },
      ...(isDev
        ? [
            {
              type: "module",
              children: `import RefreshRuntime from "/@react-refresh"
        RefreshRuntime.injectIntoGlobalHook(window)
        window.$RefreshReg$ = () => {}
        window.$RefreshSig$ = () => (type) => type
        window.__vite_plugin_react_preamble_installed__ = true;`,
            },
            { type: "module", src: "/@vite/client" },
            { type: "module", src: Client },
          ]
        : [{ type: "module", src: "/assets/entry-client.js" }]),
    ],
  }),
});
